import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Coins, Flame, HelpCircle, Plus, X, Check, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

const TIER_NAMES = {
  1: 'Casual',
  2: 'Flirty',
  3: 'Premium',
  4: 'Ultra Rare',
};

const TIER_COLORS = {
  1: '#22C55E',
  2: '#F59E0B',
  3: '#8B5CF6',
  4: '#EC4899',
};

const GiftShopPage = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [streak, setStreak] = useState(0);
  const [gifts, setGifts] = useState([]);
  const [giftsByTier, setGiftsByTier] = useState({});
  const [reactions, setReactions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [dailyClaimed, setDailyClaimed] = useState(false);
  const [expiringSoon, setExpiringSoon] = useState(0);
  const [daysUntilExpiry, setDaysUntilExpiry] = useState(null);
  const [purchasedCoins, setPurchasedCoins] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balanceRes, catalogRes, packagesRes] = await Promise.all([
        api.getCoinBalance(),
        api.getGiftCatalog(),
        api.getCoinPackages(),
      ]);

      setBalance(balanceRes.data.balance);
      setStreak(balanceRes.data.login_streak);
      setGifts(catalogRes.data.gifts);
      setGiftsByTier(catalogRes.data.by_tier);
      setReactions(catalogRes.data.reactions);
      setPackages(packagesRes.data.packages);
      setExpiringSoon(balanceRes.data.expiring_soon || 0);
      setDaysUntilExpiry(balanceRes.data.days_until_expiry);
      setPurchasedCoins(balanceRes.data.purchased_coins || 0);
    } catch (error) {
      console.error('Failed to fetch gift shop data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimDaily = async () => {
    try {
      const response = await api.claimDailyLogin();
      if (response.data.success) {
        setBalance(response.data.balance);
        setStreak(response.data.streak);
        setDailyClaimed(true);
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
        setDailyClaimed(true);
      }
    } catch (error) {
      toast.error('Failed to claim daily bonus');
    }
  };

  const handleBuyPackage = async (pkg) => {
    if (window.confirm(`Buy ${pkg.coins} coins for $${pkg.price}? (Demo mode)`)) {
      try {
        const response = await api.purchaseCoins(pkg.id);
        setBalance(response.data.balance);
        setShowBuyModal(false);
        toast.success(response.data.message);
      } catch (error) {
        toast.error('Purchase failed');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FAB710]/30 border-t-[#FAB710] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-24" data-testid="gift-shop-page">
      {/* Themed Gradient Header */}
      <div 
        className="relative pt-12 pb-6 px-4 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 50%, #061022 100%)",
          borderBottomLeftRadius: "25px",
          borderBottomRightRadius: "25px",
        }}
      >
        {/* Decorative Circles */}
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/[0.08]"></div>
        <div className="absolute bottom-3 -left-5 w-16 h-16 rounded-full bg-white/[0.05]"></div>

        {/* Top Bar */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/how-coins-work')}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="how-coins-work-btn"
              title="How coins work"
            >
              <HelpCircle className="w-5 h-5 text-white" />
            </button>
            <button 
              onClick={() => setShowBuyModal(true)}
              className="flex items-center gap-1.5 bg-[#FAB710] rounded-full px-3 py-1.5"
              data-testid="coins-btn"
            >
              <span className="text-lg">🪙</span>
              <span className="font-bold text-white">{balance}</span>
              <Plus className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Centered Icon & Title */}
        <div className="flex flex-col items-center">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mb-2 shadow-lg"
            style={{
              background: "linear-gradient(135deg, #FAB710 0%, #FFD966 100%)",
              boxShadow: "0 4px 8px rgba(250, 183, 16, 0.3)"
            }}
          >
            <Gift className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white text-center drop-shadow-sm">
            Gift Shop
          </h1>
          <p className="text-sm text-white/85 mt-1 text-center">
            Send gifts & earn coins
          </p>
        </div>

        {/* Balance Card */}
        <div className="mt-4 bg-white/10 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#FAB710] rounded-full flex items-center justify-center text-xl">
                🪙
              </div>
              <div>
                <p className="text-white/70 text-xs">Your Coins</p>
                <p className="text-2xl font-bold text-white">{balance}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1 mb-2">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-white">{streak} day streak</span>
              </div>
              <button
                onClick={handleClaimDaily}
                disabled={dailyClaimed}
                className={`block w-full text-center px-4 py-2 rounded-full font-semibold transition ${
                  dailyClaimed 
                    ? 'bg-green-500 cursor-default text-white' 
                    : 'bg-[#FAB710] hover:bg-[#e5a70f] text-white'
                }`}
                data-testid="claim-daily-btn"
              >
                {dailyClaimed ? (
                  <span className="flex items-center justify-center gap-1">
                    <Check className="w-4 h-4" /> Claimed
                  </span>
                ) : (
                  'Claim +5'
                )}
              </button>
            </div>
          </div>
          
          {/* Expiring Coins Warning */}
          {expiringSoon > 0 && daysUntilExpiry !== null && (
            <div className="mt-3 bg-red-500/20 border border-red-400/40 rounded-lg px-3 py-2 flex items-center gap-2">
              <span className="text-lg">⏰</span>
              <span className="text-sm text-red-200">
                {expiringSoon} coins expiring in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}! Use them or buy a coin pack.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-4">
        {/* Virtual Gifts Section */}
        <h2 className="text-base font-bold text-[#1C3A7A] mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#FAB710]" />
          Virtual Gifts
        </h2>
        
        {[1, 2, 3, 4].map((tierNum) => {
          const tierGifts = giftsByTier[tierNum] || [];
          if (tierGifts.length === 0) return null;
          
          return (
            <div key={tierNum} className="mb-5">
              <span 
                className="inline-block text-xs font-semibold text-white px-3 py-1 rounded-lg mb-3"
                style={{ backgroundColor: TIER_COLORS[tierNum] }}
              >
                {TIER_NAMES[tierNum]}
              </span>
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {tierGifts.map((gift) => (
                  <div 
                    key={gift.id}
                    className="flex-shrink-0 w-28 bg-white rounded-2xl p-4 text-center shadow-sm border-2 border-transparent hover:border-[#FAB710] transition cursor-pointer"
                    data-testid={`gift-${gift.id}`}
                  >
                    <div className="text-4xl mb-2">{gift.emoji}</div>
                    <p className="text-sm font-semibold text-gray-800">{gift.name}</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <span className="text-amber-500">🪙</span>
                      <span className="font-bold text-amber-600">{gift.cost}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{gift.meaning}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Chat Reactions */}
        <h2 className="text-base font-bold text-[#1C3A7A] mb-3 mt-6 flex items-center gap-2">
          <span className="text-lg">💬</span>
          Chat Reactions
        </h2>
        <div className="grid grid-cols-4 gap-3 mb-6">
          {reactions.map((reaction) => (
            <div 
              key={reaction.id}
              className="bg-white rounded-xl p-4 text-center shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`reaction-${reaction.id}`}
            >
              <div className="text-3xl mb-1">{reaction.emoji}</div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-amber-500 text-sm">🪙</span>
                <span className="text-sm font-semibold text-amber-600">{reaction.cost}</span>
              </div>
            </div>
          ))}
        </div>

        {/* How to Earn */}
        <h2 className="text-base font-bold text-[#1C3A7A] mb-3 flex items-center gap-2">
          <span className="text-lg">🎁</span>
          Earn Free Coins
        </h2>
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-6">
          <div className="space-y-3">
            {[
              { icon: '📅', text: 'Daily login: +5 coins' },
              { icon: '🔥', text: '7-day streak: +25 bonus' },
              { icon: '👤', text: 'Complete profile: +50 coins' },
              { icon: '💬', text: 'First message: +10 coins' },
              { icon: '🎁', text: 'Receive a gift: +2 coins' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Buy Coins Modal */}
      {showBuyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={() => setShowBuyModal(false)}>
          <div 
            className="bg-white rounded-t-3xl w-full max-w-lg p-6 pb-10"
            onClick={e => e.stopPropagation()}
            data-testid="buy-coins-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#1C3A7A]">Buy Coins</h2>
              <button onClick={() => setShowBuyModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-3">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => handleBuyPackage(pkg)}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl transition ${
                    pkg.id === 'popular' 
                      ? 'bg-amber-50 border-2 border-amber-400' 
                      : pkg.id === 'best_value'
                      ? 'bg-green-50 border-2 border-green-400'
                      : 'bg-gray-100 border-2 border-transparent hover:border-gray-300'
                  }`}
                  data-testid={`buy-package-${pkg.id}`}
                >
                  <div className="relative">
                    {pkg.id === 'popular' && (
                      <span className="absolute -top-3 left-0 text-xs font-bold text-white bg-amber-500 px-2 py-0.5 rounded">
                        POPULAR
                      </span>
                    )}
                    {pkg.id === 'best_value' && (
                      <span className="absolute -top-3 left-0 text-xs font-bold text-white bg-green-500 px-2 py-0.5 rounded">
                        BEST VALUE
                      </span>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-2xl">🪙</span>
                      <span className="text-2xl font-bold text-gray-800">{pkg.coins}</span>
                    </div>
                    {pkg.bonus > 0 && (
                      <span className="text-sm text-green-600 font-semibold">+{pkg.bonus}% bonus</span>
                    )}
                  </div>
                  <span className="text-xl font-bold text-[#1C3A7A]">${pkg.price}</span>
                </button>
              ))}
            </div>
            
            <p className="text-xs text-gray-400 text-center mt-4">
              * Demo mode - In production, purchases use Apple/Google Pay
            </p>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default GiftShopPage;
